import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Phone,
  EnvelopeSimple,
  ChatTeardrop,
  Person,
  CircleNotch,
  ClockCounterClockwise,
  Plus,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fetchChaseLogs, addChaseLog, type ChaseLog } from '@/lib/feeNotesApi';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/lib/api';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

const METHODS = [
  { value: 'email',     label: 'Email',        icon: <EnvelopeSimple className="h-4 w-4" /> },
  { value: 'phone',     label: 'Phone call',   icon: <Phone className="h-4 w-4" /> },
  { value: 'letter',    label: 'Letter',       icon: <ChatTeardrop className="h-4 w-4" /> },
  { value: 'in_person', label: 'In person',    icon: <Person className="h-4 w-4" /> },
] as const;

const METHOD_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone call',
  letter: 'Letter',
  in_person: 'In person',
};

interface Props {
  feeNoteId: string;
  feeNoteNumber: string;
  onClose: () => void;
}

export function FeeChaseLogModal({ feeNoteId, feeNoteNumber, onClose }: Props) {
  const qc = useQueryClient();
  const [method, setMethod] = useState<string>('email');
  const [notes, setNotes] = useState('');
  const [contactedAt, setContactedAt] = useState(() => new Date().toISOString().slice(0, 10));

  const logsQ = useQuery({
    queryKey: ['fee-chase-log', feeNoteId],
    queryFn: () => fetchChaseLogs(feeNoteId),
    staleTime: 0,
  });

  const addMut = useMutation({
    mutationFn: () => addChaseLog(feeNoteId, { method, notes: notes.trim() || undefined, contactedAt }),
    onSuccess: () => {
      toast.success('Chase contact logged');
      setNotes('');
      setContactedAt(new Date().toISOString().slice(0, 10));
      qc.invalidateQueries({ queryKey: ['fee-chase-log', feeNoteId] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not log chase')),
  });

  const logs: ChaseLog[] = logsQ.data ?? [];

  return (
    <>
      <ScrollLock />
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-base font-semibold text-foreground">Chase contact log</div>
            <div className="text-xs text-muted-foreground">{feeNoteNumber}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            ×
          </button>
        </div>

        <div className="flex max-h-[70vh] flex-col overflow-y-auto">
          {/* Log new chase form */}
          <div className="border-b border-border p-5">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Log new contact</div>

            {/* Method selector */}
            <div className="mb-3 grid grid-cols-4 gap-1.5">
              {METHODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMethod(m.value)}
                  className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2.5 text-xs transition ${
                    method === m.value
                      ? 'border-brand-600 bg-brand-50 text-brand-700 dark:text-brand-300'
                      : 'border-border text-muted-foreground hover:border-brand-300 hover:bg-muted/40'
                  }`}
                >
                  {m.icon}
                  <span className="font-medium">{m.label}</span>
                </button>
              ))}
            </div>

            {/* Date + notes */}
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Date of contact
                </label>
                <input
                  type="date"
                  value={contactedAt}
                  onChange={(e) => setContactedAt(e.target.value)}
                  className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Notes (optional)
              </label>
              <ExpandableTextarea
                label="Notes"
                value={notes}
                onChange={setNotes}
                placeholder="e.g. Spoke to accounts payable — promised payment within 7 days"
                rows={2}
              />
            </div>

            <Button
              variant="primary"
              size="sm"
              disabled={addMut.isPending}
              leftIcon={addMut.isPending ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              onClick={() => addMut.mutate()}
            >
              {addMut.isPending ? 'Logging…' : 'Log contact'}
            </Button>
          </div>

          {/* Chase history */}
          <div className="p-5">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <ClockCounterClockwise className="h-3.5 w-3.5" />
              History ({logs.length})
            </div>

            {logsQ.isLoading ? (
              <div className="flex items-center justify-center py-6">
                <CircleNotch className="h-5 w-5 animate-spin text-muted-foreground/40" />
              </div>
            ) : logs.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground/60">
                No chase contacts logged yet
              </div>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div key={log.id} className="rounded-md border border-border bg-muted/20 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="rounded-md bg-brand-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">
                          {METHOD_LABELS[log.method] ?? log.method}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.contactedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {log.loggedBy.firstName} {log.loggedBy.lastName}
                      </span>
                    </div>
                    {log.notes && (
                      <p className="mt-1.5 text-xs text-foreground/80">{log.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
